import {
  Anchor,
  Badge,
  Card,
  Code,
  Flex,
  Grid,
  Table,
  Text,
  useMantineColorScheme,
} from "@mantine/core";
import { useShow } from "@refinedev/core";
import { Show } from "@refinedev/mantine";
import {
  DetailsCard,
  DetailsUserCard,
} from "components/common/show/DetailsPair";
import { History } from "components/common/show/History";
import { Invoice } from "entities/invoice";
import { InvoiceAddress } from "entities/invoice/invoice_address";
import { colorsByKind as colorsByLineItemKind } from "entities/invoice/invoice_line_item";
import { colorsBySource } from "entities/invoice/source";
import { colorsByStatus } from "entities/invoice/status";
import { Link } from "react-router-dom";
import { getInvoiceUrl } from "utils/chargebee";

export const InvoiceShow = () => {
  const { queryResult } = useShow<Invoice>();
  const { data, isLoading } = queryResult;
  const invoice = data?.data;
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  if (!invoice) return <div />;

  return (
    <Show isLoading={isLoading}>
      <Grid>
        <Grid.Col span={8}>
          <Flex gap="sm" direction="column">
            <DetailsCard title="Invoice Line Items">
              <Table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Kind</th>
                    <th>Quantity</th>
                    <th>Unit Amount</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.line_items.map((lineItem) => (
                    <>
                      <tr key={lineItem.id}>
                        <td>{lineItem.id}</td>
                        <td>
                          <Badge color={colorsByLineItemKind[lineItem.kind]}>
                            {lineItem.kind}
                          </Badge>
                        </td>
                        <td>{lineItem.quantity}</td>
                        <td>${(lineItem.unit_amount / 100).toFixed(2)}</td>
                        <td>{lineItem.description}</td>
                      </tr>
                      {lineItem?.discount && (
                        <td colSpan={5} style={{ paddingLeft: 50 }}>
                          <Card
                            p="xs"
                            bg={isDark ? "gray.8" : "gray.2"}
                            style={{ width: "100%", textAlign: "center" }}
                            m="xs"
                          >
                            <Table style={{ width: "100%" }}>
                              <thead>
                                <th>Coupon ID</th>
                                <th>Discount Type</th>
                                <th>Discount Amount</th>
                                <th>Entity ID</th>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>{lineItem.discount.coupon_id}</td>
                                  <td>
                                    <Badge>
                                      {lineItem.discount.discount_type}
                                    </Badge>
                                  </td>
                                  <td>
                                    $
                                    {(
                                      lineItem.discount.discount_amount / 100
                                    ).toFixed(2)}
                                  </td>
                                  <td>{lineItem.discount.entity_id}</td>
                                </tr>
                              </tbody>
                            </Table>
                          </Card>
                        </td>
                      )}
                    </>
                  ))}
                </tbody>
              </Table>
            </DetailsCard>
            <DetailsCard title="Invoice discounts">
              <Table style={{ textAlign: "center" }} striped>
                <thead>
                  <th>Discount Type</th>
                  <th>Discount Amount</th>
                  <th>Entity ID</th>
                  <th>Entity Type</th>
                </thead>
                <tbody>
                  {invoice.discounts.map((discount) => (
                    <tr>
                      <td>
                        <Badge>{discount.discount_type}</Badge>
                      </td>
                      <td>${(discount.amount / 100).toFixed(2)}</td>
                      <td>{discount.entity_id}</td>
                      <td>
                        <Badge>{discount.entity_type}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </DetailsCard>
            <History<Invoice> resources={"invoices"} entityId={invoice.id} />
          </Flex>
        </Grid.Col>
        <Grid.Col span={4}>
          <Flex direction="column" gap="sm">
            <DetailsUserCard user={invoice.user} />
            <DetailsCard title="ID">
              <Text>{invoice.id}</Text>
            </DetailsCard>
            <DetailsCard title="Remote ID">
              <Anchor component={Link} to={getInvoiceUrl(invoice.remote_id)}>
                {invoice.remote_id}
              </Anchor>
            </DetailsCard>
            <DetailsCard title="Subscription">
              <Anchor
                component={Link}
                to={`/subscriptions/show/${invoice.subscription?.id}`}
              >
                {invoice.subscription?.remote_id}
              </Anchor>
            </DetailsCard>
            <DetailsCard title="Source">
              <Badge color={colorsBySource[invoice.source]}>
                {invoice.status}
              </Badge>
            </DetailsCard>
            <DetailsCard title="Status">
              <Badge color={colorsByStatus[invoice.status]}>
                {invoice.status}
              </Badge>
            </DetailsCard>
            <DetailsCard title="Amount Paid">
              <Text>${(invoice.amount_paid / 100).toFixed(2)}</Text>
            </DetailsCard>
            <DetailsCard title="Paid At">
              <Code>{invoice.paid_at?.toString()}</Code>
            </DetailsCard>
            <AddressCard
              title="Shipping Address"
              address={invoice.shipping_address}
            />
            <AddressCard
              title="Billing Address"
              address={invoice.billing_address}
            />
            <DetailsCard title="Last Synced At">
              <Code>{invoice.last_synced_at?.toString()}</Code>
            </DetailsCard>
          </Flex>
        </Grid.Col>
      </Grid>
    </Show>
  );
};

function AddressCard({
  title,
  address,
}: {
  title: string;
  address: InvoiceAddress;
}) {
  return (
    <DetailsCard title={title}>
      <Flex direction="column" gap="sm">
        <AddressCardItem title="First Name">
          {address.first_name}
        </AddressCardItem>
        <AddressCardItem title="Last Name">{address.last_name}</AddressCardItem>
        <AddressCardItem title="Email">{address.email}</AddressCardItem>
        <AddressCardItem title="Company">{address.company}</AddressCardItem>
        <AddressCardItem title="Phone">{address.phone}</AddressCardItem>
        <AddressCardItem title="Line 1">{address.line1}</AddressCardItem>
        <AddressCardItem title="Line 2">{address.line2}</AddressCardItem>
        <AddressCardItem title="Line 3">{address.line3}</AddressCardItem>
        <AddressCardItem title="City">{address.city}</AddressCardItem>
        <AddressCardItem title="State Code">
          {address.state_code}
        </AddressCardItem>
        <AddressCardItem title="State">{address.state}</AddressCardItem>
        <AddressCardItem title="Country">{address.country}</AddressCardItem>
        <AddressCardItem title="Zip">{address.zip}</AddressCardItem>
      </Flex>
    </DetailsCard>
  );
}

function AddressCardItem({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  return (
    <div>
      <Text weight="bold" size="xs" color={isDark ? "gray.6" : "gray.8"}>
        {title}
      </Text>
      <Text italic>{children}</Text>
    </div>
  );
}
